import { ChangeEvent, ReactText, forwardRef } from 'react'
import classNames from 'classnames'
import useId from '../hooks/useId'

interface Props {
  className?: string
  label: string
  name?: string
  role?: string
  placeholder?: string
  type: 'text' | 'email' | 'password' | 'number' | 'hidden'
  value?: ReactText
  errorMessage?: string
  autoComplete?: string
  disabled?: boolean
  error?: boolean
  required?: boolean
  hideLabel?: boolean
  assistiveText?: string | React.ReactNode
  leftEl?: React.ReactNode
  rightEl?: React.ReactNode
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  onBlur?: () => void
  testId?: string
}

const Input = forwardRef(
  (
    {
      className,
      label,
      name,
      role,
      placeholder,
      type = 'text',
      value,
      errorMessage,
      autoComplete = 'on',
      disabled = false,
      error = false,
      required,
      hideLabel,
      assistiveText,
      leftEl,
      rightEl,
      onChange,
      onBlur,
      testId,
    }: Props,
    ref: React.Ref<HTMLInputElement>
  ) => {
    const id = useId('Input')

    if (type === 'hidden') {
      return <input id={id} name={name ?? label} type={type} value={value} />
    }

    return (
      <div className={classNames('Input', className)}>
        <div className="Input__labelWrapper">
          <label
            className={classNames('Input__label', {
              'screen-reader-text': hideLabel,
            })}
            htmlFor={id}
          >
            {label}
          </label>
          {typeof assistiveText === 'string' ? (
            <div className="Input__assistiveText">{assistiveText}</div>
          ) : (
            assistiveText
          )}
        </div>
        <div className="Input__inputWrapper">
          <input
            className={classNames('Input__input', {
              error: error,
              withRightEl: !!rightEl,
            })}
            ref={ref}
            id={id}
            name={name ?? label}
            type={type}
            role={role}
            placeholder={placeholder}
            value={value}
            autoComplete={autoComplete}
            aria-invalid={error}
            disabled={disabled}
            onChange={onChange}
            onBlur={onBlur}
            required={required}
            data-testid={testId}
          />
          {leftEl && <div className="Input__leftEl">{leftEl}</div>}
          {rightEl && <div className="Input__rightEl">{rightEl}</div>}
        </div>
        {error && errorMessage && <p className="Input__message">{errorMessage}</p>}
      </div>
    )
  }
)

export default Input
